/**
 * Drago Bundle Selector
 * Custom element that lets users pick a kit (Roller, Roller+1Wrap, Roller+2Wraps)
 * and adds the right products to the cart with a single click.
 * Wrap discounts are handled automatically by the Shopify "50% off with Roller" automatic discount.
 */
class BundleSelector extends HTMLElement {
  constructor() {
    super();
    this.rollerVariant = parseInt(this.dataset.rollerVariant, 10);
    this.wrapsVariant = parseInt(this.dataset.wrapsVariant, 10);
    this.cta = this.querySelector('[data-bundle-add]');
    this.buyNow = this.querySelector('[data-bundle-buy-now]');
    this.ctaText = this.querySelector('.bundle-selector__cta-text');
    this.ctaLoading = this.querySelector('.bundle-selector__cta-loading');
    this.errorEl = this.querySelector('[data-bundle-error]');
    this.radios = this.querySelectorAll('.bundle-option__radio');
  }

  connectedCallback() {
    // Default selection: Roller + 1 Wrap (the highlighted "Most Popular")
    const defaultRadio = this.querySelector('.bundle-option__radio[value="roller-1wrap"]');
    if (defaultRadio) defaultRadio.checked = true;

    this.cta.addEventListener('click', () => this.handleAddToCart(false));
    if (this.buyNow) {
      this.buyNow.addEventListener('click', () => this.handleAddToCart(true));
    }
  }

  getSelectedKit() {
    const checked = this.querySelector('.bundle-option__radio:checked');
    return checked ? checked.value : 'roller-1wrap';
  }

  buildItems(kit) {
    switch (kit) {
      case 'roller':
        return [{ id: this.rollerVariant, quantity: 1 }];
      case 'roller-1wrap':
        return [
          { id: this.rollerVariant, quantity: 1 },
          { id: this.wrapsVariant, quantity: 1 }
        ];
      case 'roller-2wraps':
        return [
          { id: this.rollerVariant, quantity: 1 },
          { id: this.wrapsVariant, quantity: 2 }
        ];
      default:
        return [{ id: this.rollerVariant, quantity: 1 }];
    }
  }

  setLoading(loading) {
    this.cta.disabled = loading;
    this.ctaText.hidden = loading;
    this.ctaLoading.hidden = !loading;
  }

  showError(message) {
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  hideError() {
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  async handleAddToCart(goToCheckout) {
    this.hideError();
    this.setLoading(true);

    const kit = this.getSelectedKit();
    const items = this.buildItems(kit);

    // Use the theme's cart drawer for rendering (same pattern as product-form.js)
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const cartAddUrl = `${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`;

    try {
      // Single POST: add all kit items in one request. Shopify's automatic
      // discount engine evaluates the cart state after the mutation, so the
      // 50%-off-wraps Bundle and 15%-off-second-roller BOGO still trigger
      // correctly even though we no longer add items sequentially.
      const body = { items };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      }

      const resp = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
        body: JSON.stringify(body)
      });
      const finalData = await resp.json();
      if (!resp.ok || finalData.status) {
        throw new Error(finalData.description || finalData.message || 'Could not add kit to cart');
      }

      if (goToCheckout) {
        window.location.href = '/checkout';
        return;
      }

      // Let the theme's cart drawer handle the render (proper pattern)
      if (cart && typeof cart.renderContents === 'function') {
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        cart.renderContents(finalData);
      } else {
        window.location.reload();
      }
    } catch (e) {
      console.error('Bundle add to cart failed:', e);
      this.showError(e.message || 'Something went wrong. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }
}

if (!customElements.get('bundle-selector')) {
  customElements.define('bundle-selector', BundleSelector);
}
